import {
  AircraftStatusQueryResult,
  IAircraft,
  IAircraftLastStatus,
  IAircraftMaintenance,
  IAirport,
  ICrewBase,
  IFlightAircraft,
  IStructure,
  IUser,
} from "@vmosolutions/oc-interfaces";
import { API, Auth } from "aws-amplify";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { IRootState } from "../reducers/CombinedReducers";
import { IAircraftStatusMap, IAirportOption } from "../reducers/StructuresReducer";
import { openSnackbar } from "./App.Actions";

export enum Type {
  GOT_AIRPORTS = "GOT_AIRPORTS",
  GOT_STRUCTURES = "GOT_STRUCTURES",
  SORT_AIRCRAFT = "SORT_AIRCRAFT",
  STORE_SEARCHED_AIRPORTS = "STORE_SEARCHED_AIRPORTS",
  GOT_APP_USERS = "GOT_APP_USERS",
  GOT_AIRCRAFT_STATUS = "GOT_AIRCRAFT_STATUS",
  GOT_AIRCRAFT_MAINTENANCE_UPDATE = "GOT_AIRCRAFT_MAINTENANCE_UPDATE",
}
export interface IGotAirports {
  type: Type.GOT_AIRPORTS;
  airportOptions: IAirportOption[];
  searchStr: string;
}

export interface IGotStructures {
  type: Type.GOT_STRUCTURES;
  customerStructures: IStructure;
}

export interface ISortAircraft {
  type: Type.SORT_AIRCRAFT;
  aircraft: IAircraft[];
}

export interface IGotAppUsers {
  type: Type.GOT_APP_USERS;
  appUsers: IUser[];
}

export interface IGotAircraftStatus {
  type: Type.GOT_AIRCRAFT_STATUS;
  aircraftStatus: IAircraftStatusMap;
  updateAircraftId?: string;
}

export interface IGotAircraftMaintenanceUpdate {
  type: Type.GOT_AIRCRAFT_MAINTENANCE_UPDATE;
  registration: string;
  maintenance?: IAircraftMaintenance;
}

export type Action =
  | IGotAirports
  | ISortAircraft
  | IGotStructures
  | IGotAppUsers
  | IGotAircraftStatus
  | IGotAircraftMaintenanceUpdate;

/**
 * Call is made in the AppRoutes (right after structures call) and loads in
 * @returns void - dispatch common airports to the airport options in structures.
 */
export const getCustomerCustomAirports = (): ThunkAction<Promise<string>, object, object, IGotStructures> => {
  return async (dispatch: ThunkDispatch<object, object, any>, getState: any): Promise<any> => {
    const commonAirports = await AmplifyAPIWrapper.Get("/structures/commonAirports");
    // If there are common airports found, dispatch them to redux airport object.
    if (Array.isArray(commonAirports) && commonAirports?.length > 0) {
      // Remap IAirport to IAirportOption type.
      const airportOptions = commonAirports.map((airport: IAirport) => ({
        _id: airport._id,
        ident: airport.Ident,
        name: airport.Name,
        timezoneId: airport.timezoneId,
        iata: airport.IATACode,
        gates: airport.gates ?? [],
      }));
      dispatch({ type: Type.GOT_AIRPORTS, airportOptions });
    }
  };
};

export const gotAircraftMaintenanceUpdate = (
  registration: string,
  maintenance?: IAircraftMaintenance
): IGotAircraftMaintenanceUpdate => {
  return { type: Type.GOT_AIRCRAFT_MAINTENANCE_UPDATE, registration, maintenance };
};

export const addAirportToList = (searchStr: string, airportsToAdd: IAirportOption[]): IGotAirports => {
  return { type: Type.GOT_AIRPORTS, airportOptions: airportsToAdd, searchStr };
};

// Retrieve master selection lists (used to populate selection lists inside the flight editor).
export const getCustomerStructures = (): ThunkAction<Promise<string>, object, object, IGotStructures> => {
  return async (dispatch: ThunkDispatch<object, object, any>, getState: any): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    const customerStructures: any = await API.get("skytrains", "/structures", {
      headers: { Accept: "*/*", Authorization: token },
    });
    if (!customerStructures) {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Unable to retrieve necessary information, please contact an administrator for assistance.",
        })
      );
      return;
    }

    /** Sort User list Alphabetically */
    // Sorting options are defined in TimelineSortFilter.tsx, if default here is changed, modify it there too.
    const sortAircraftByRegistration = (a: IFlightAircraft, b: IFlightAircraft): number => {
      return b.registration > a.registration ? -1 : 1;
    };

    // If customer structures.aircraft comes back as anything but an array, set it to an empty array.
    // Resolved an issue where admin section was creashing due to this value not being an array.
    // See Jira ticket OCN-389
    if (!Array.isArray(customerStructures?.aircraft)) {
      customerStructures.aircraft = [];
    }

    // Set default sorting to registration descending.
    customerStructures.aircraft = customerStructures.aircraft
      .filter((aircraft: IAircraft) => aircraft.isActive)
      .sort(sortAircraftByRegistration);

    // customerStructures.delayCategories = customerStructures.delayCategories.sort(
    //   (a: IDelayCategory, b: IDelayCategory) => a.title.localeCompare(b.title)
    // );
    // customerStructures.delayCategories = customerStructures.delayCategories.map((cat: IDelayCategory) => cat.delayCodes.sort(( a: IDelayCode, b: IDelayCode) => a> b? -1: 1 )

    dispatch({ type: Type.GOT_STRUCTURES, customerStructures });
  };
};

export const sortAircraft = (): ThunkAction<void, IRootState, object, ISortAircraft> => {
  return (dispatch: ThunkDispatch<object, object, ISortAircraft>, getState: () => IRootState): void => {
    const aircraft = [...getState().structures.customerStructure.aircraft];
    aircraft.sort((a: IAircraft, b: IAircraft) => a.registration.localeCompare(b.registration));
    dispatch({ type: Type.SORT_AIRCRAFT, aircraft: [...aircraft] });
  };
};

export const getCrewBases = async (): Promise<ICrewBase[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const crewBases: any = await API.get("skytrains", "/structures/crewBases", {
      headers: { Accept: "*/*", Authorization: token },
    });
    return crewBases;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const storeAppUsers = (appUsers: IUser[]): ThunkAction<void, object, object, IGotStructures> => {
  return (dispatch: ThunkDispatch<object, object, any>, getState: any): void => {
    if (Array.isArray(appUsers) && appUsers.length > 0) {
      dispatch({ type: Type.GOT_APP_USERS, appUsers });
    }
  };
};

/** Use the loaded aircraft list to generate the initial aircraft status map */
export const createAircraftStatusMap = (aircraft: IAircraft[]): IAircraftStatusMap => {
  if (aircraft?.length) {
    const aircraftStatusMap = aircraft.reduce((obj: { [key: string]: any }, AC) => {
      const aircraftStatus: IAircraftLastStatus = {
        acid: AC._id,
        registration: AC.registration,
        serialNumber: AC.serialNumber,
        // Fields below are left null and filled by get query then websocket updated.
        lastUpdated: null,
        aircraft: null,
        address: null,
        traceId: null,
        flightId: null,
        airport: null,
        state: null,
        milliSinceLastUpdate: null,
        flight: null,
      };
      const lastStatus: IAircraftLastStatus = obj[AC._id];
      if (!lastStatus) obj[AC._id] = aircraftStatus;
      return obj;
    }, {});
    return aircraftStatusMap;
  }
  return {};
};

/**
 * Call is made in the AppRoutes (right after structures call) and loads in
 * @returns void - dispatch common airports to the airport options in structures.
 */
export const getAircraftStatuses = (): ThunkAction<Promise<string>, object, object, IGotStructures> => {
  return async (dispatch: ThunkDispatch<object, object, any>, getState: any): Promise<any> => {
    // Retrieve current aircraft lift and create a map to store future status updates.
    const aircraftList = getState().structures.customerStructure.aircraft;
    // Nothing to ask about until the customer structures have loaded.
    if (!aircraftList?.length) return;
    const aircraftMap = createAircraftStatusMap(aircraftList);
    const response = await AmplifyAPIWrapper.Get<AircraftStatusQueryResult[]>("/aircraft/status", null, null);
    if (response && Array.isArray(response)) {
      const aircraftStatusMap = response.reduce((obj: { [key: string]: any }, result) => {
        const lastStatus: AircraftStatusQueryResult = obj[result._id];

        // If available, use updateAt from state update, otherwise use current time.
        const lastUpdated = result?.status?.state?.updatedAt
          ? new Date(result?.status?.state?.updatedAt)?.valueOf()
          : new Date()?.valueOf();

        const currentStatus: IAircraftLastStatus = {
          acid: result._id,
          registration: result.registration,
          serialNumber: result.serialNumber,
          lastUpdated,
          aircraft: result?.status?.aircraft ?? null,
          address: result?.status?.address ?? null,
          traceId: result?.status?.traceId ?? null,
          flightId: result?.status?.flightId ?? null,
          airport: result?.status?.airport ?? null,
          state: result?.status?.state ?? null,
          milliSinceLastUpdate: null,
          flight: null,
        };

        if (!lastStatus) {
          obj[result._id] = currentStatus;
        } else {
          obj[result._id] = { ...lastStatus, ...currentStatus }; // Spread new info into old.
        }
        return obj;
      }, {});
      const updatedStatusMap = { ...(aircraftMap ?? {}), ...aircraftStatusMap };
      // console.log("aircraft status updated via get call.", updatedStatusMap);
      dispatch({ type: Type.GOT_AIRCRAFT_STATUS, aircraftStatus: updatedStatusMap });
    }
  };
};

export const gotAircraftStatus = (
  aircraftStatus: IAircraftStatusMap,
  updateAircraftId?: string
): IGotAircraftStatus => {
  return { type: Type.GOT_AIRCRAFT_STATUS, aircraftStatus, updateAircraftId };
};
